import { validateComponentGenerationPlanV2, type ComponentGenerationPlanV2, type PrepareComponentGenerationPlanV2Input } from './componentGenerationPlanV2.js';
import { canonicalJson } from '@djgg/gameplay-kit';
import { validateAcceptedCompositionV2, type AcceptedCompositionV2, type CompositionPredecessorContextV2 } from './spec/compositionV2.js';
import { compositionPrivatePipelineSnapshotV2 } from './buildPipelines.js';
import type { AcceptedCompositionV1 } from './spec/gameSpec.js';
import type { CompositionLineageV1, CompositionRevisionV1 } from './compositionLineage.js';
import { PlatformError } from '@djgg/core';
import {
  resolveBuildPipeline,
  composedCourierPipelineSnapshot,
  composedMaintainedPipelineSnapshot,
  isComposedPipeline,
  measuredFirstPlayablePipelineSnapshot,
  verifiedCorePipelineSnapshot,
  type BuildPipelineCatalog,
  type BuildPipelineRef,
  type BuildPipelineSnapshot,
} from './buildPipelines.js';
import type { AcceptedQualityRunV1, GameQualityExecutionKind } from './gameQualityContract.js';
import type { QualityAdmissionV2 } from './qualitySchemas.js';

export type BuildAcceptancePolicyMode = 'legacy' | 'verified-core' | 'verified-core-v2' | 'measured-first-playable' | 'paused';

export interface AcceptedBuildExecutionPolicyV1 {
  compositionV2?: { route: 'private-composition-v2'; authority: AcceptedCompositionV2; releaseId: string } & (
    { kind?: never; pendingResolution?: never; generationPolicyDigest?: never } |
    { kind: 'bounded-component-generation'; pendingResolution: ComponentGenerationPlanV2; generationPolicyDigest: string }
  );
  composition?: AcceptedCompositionV1;
  /** Required for a composed successor command. The accepted composition is
   * the predecessor's accepted game re-admitted on the current released
   * runtime; this separately binds the predecessor (and the runtime it was
   * admitted on) and media provenance for the fork/rebind action. */
  compositionLineage?: CompositionLineageV1;
  /** Required for a composed REVISION command (`composition-revision`): the
   * accepted composition is a new game replacing this live predecessor, with
   * the art it carries. Never beside lineage. */
  compositionRevision?: CompositionRevisionV1;
  schemaVersion: 1;
  executionKind: GameQualityExecutionKind;
  acceptanceMode: BuildAcceptancePolicyMode;
  pipelineSnapshot: BuildPipelineSnapshot;
  providerPolicy: 'normal' | 'zero-provider' | 'originating-run-only' | 'bounded-component-generation';
  sourceIdentityRequired: boolean;
  legacyProofDebt: string[];
  originatingRun?: {
    inputDigest: string;
    contractDigest: string;
    coreSourceDigest: string;
    mediaBasisDigest: string;
  };
}

const CODE_KINDS = new Set<GameQualityExecutionKind>([
  'code-create', 'composition-conversion', 'composition-revision', 'code-iterate', 'code-fork-remix', 'code-recovery',
]);

export function isCodeExecutionKind(kind: GameQualityExecutionKind): boolean {
  return CODE_KINDS.has(kind);
}

export function resolveBuildExecutionPolicy(input: {
  composition?: AcceptedCompositionV1;
  compositionLineage?: CompositionLineageV1;
  compositionRevision?: CompositionRevisionV1;
  catalog: BuildPipelineCatalog;
  mode: BuildAcceptancePolicyMode;
  executionKind: GameQualityExecutionKind;
  requested?: BuildPipelineRef;
  active?: BuildPipelineSnapshot;
  acceptedPolicy?: AcceptedBuildExecutionPolicyV1;
  qualityRun?: AcceptedQualityRunV1 | null;
  qualityAdmission?: QualityAdmissionV2 | null;
}): AcceptedBuildExecutionPolicyV1 {
  if (input.executionKind === 'accepted-command-redrive') {
    if (!input.acceptedPolicy) throw new PlatformError('validation', 'accepted redrive requires its immutable execution policy');
    if (input.acceptedPolicy.compositionV2) throw new PlatformError('validation', 'V2 private redrive requires its distinct durability route');
    return structuredClone(input.acceptedPolicy);
  }
  if (input.mode === 'paused') {
    throw new PlatformError('unavailable', 'new build acceptance is paused; existing accepted commands remain resumable');
  }
  const code = isCodeExecutionKind(input.executionKind);
  let compositionLineage: CompositionLineageV1 | undefined;
  if (input.composition) {
    const expectedLineageKind = input.executionKind === 'source-preserving-copy'
      ? 'fork-copy'
      : input.executionKind === 'creator-asset-rebind'
        ? 'asset-rebind'
        : undefined;
    if (input.mode !== 'measured-first-playable'
      || (!expectedLineageKind && input.executionKind !== 'code-create'
        && input.executionKind !== 'composition-conversion' && input.executionKind !== 'composition-revision')
      || (expectedLineageKind ? input.compositionLineage?.kind !== expectedLineageKind : !!input.compositionLineage)) {
      throw new PlatformError('validation', 'Composed successor requires matching immutable lineage');
    }
    if ((input.executionKind === 'composition-revision') !== !!input.compositionRevision
      || (input.compositionRevision && (input.compositionRevision.authorityDigest !== input.composition.digest
        || !/^[a-f0-9]{64}$/.test(input.compositionRevision.digest)))) {
      throw new PlatformError('validation', 'Composed revision requires its matching immutable revision authority');
    }
    if (input.compositionLineage) {
      if (input.compositionLineage.authorityDigest !== input.composition.digest
        || !/^[a-f0-9]{64}$/.test(input.compositionLineage.digest)) {
        throw new PlatformError('validation', 'Composition lineage does not match accepted authority');
      }
      compositionLineage = structuredClone(input.compositionLineage);
    }
  } else if (input.compositionLineage || input.compositionRevision) {
    throw new PlatformError('validation', 'Composition lineage or revision requires accepted composition authority');
  }
  const verifiedMode = input.mode === 'verified-core' || input.mode === 'verified-core-v2';
  const pipelineSnapshot = input.composition ? (input.composition.spec.profile === 'courier-short@1' ? composedCourierPipelineSnapshot(input.catalog) : composedMaintainedPipelineSnapshot(input.catalog)) : input.mode === 'measured-first-playable' && code
    ? input.active?.id === 'measured-first-playable'
      ? resolveBuildPipeline(input.catalog, undefined, input.active)
      : measuredFirstPlayablePipelineSnapshot(input.catalog)
    : verifiedMode && code
    ? verifiedCorePipelineSnapshot(input.catalog)
    : resolveBuildPipeline(input.catalog, input.requested, input.active);
  if (input.mode === 'verified-core' && code) {
    if (!input.qualityRun || input.qualityRun.stage !== 'contract-pending'
      || input.qualityRun.recipe.id !== 'verified-core'
      || input.qualityRun.recipe.revision !== 1
      || input.qualityRun.executionKind !== input.executionKind) {
      throw new PlatformError('validation', 'verified-core code acceptance requires matching immutable contract-pending inputs');
    }
  }
  if (input.mode === 'verified-core-v2' && code) {
    if (!input.qualityAdmission
      || input.qualityAdmission.recipe.id !== 'verified-core'
      || input.qualityAdmission.recipe.revision !== 2
      || input.qualityAdmission.executionKind !== input.executionKind) {
      throw new PlatformError('validation', 'verified-core-v2 code acceptance requires matching immutable admission authority');
    }
  }
  if ((isComposedPipeline(pipelineSnapshot) || pipelineSnapshot.config?.assembly !== undefined) !== !!input.composition) throw new PlatformError('validation', 'Composition recipe requires matching accepted authority');
  const origin = input.qualityRun?.core && input.qualityRun.spec ? {
    inputDigest: input.qualityRun.originalInputs.inputDigest,
    contractDigest: input.qualityRun.spec.contractDigest,
    coreSourceDigest: input.qualityRun.core.sourceDigest,
    mediaBasisDigest: input.qualityRun.core.mediaBasisDigest,
  } : undefined;
  if (input.executionKind === 'automatic-art-completion' && input.mode === 'verified-core' && !origin) {
    throw new PlatformError('validation', 'verified-core art completion requires the originating verified core and media basis');
  }
  const providerPolicy = input.composition ? 'zero-provider' as const : input.executionKind === 'creator-asset-rebind'
    ? 'zero-provider' as const
    : input.executionKind === 'automatic-art-completion'
      ? 'originating-run-only' as const
      : 'normal' as const;
  const sourceIdentityRequired = input.composition ? false : !code;
  const legacyProofDebt = input.composition ? [] : !code && !origin ? ['legacy-gameplay-proof-unavailable'] : [];
  return {
    schemaVersion: 1,
    executionKind: input.executionKind,
    acceptanceMode: input.mode,
    pipelineSnapshot,
    providerPolicy,
    sourceIdentityRequired,
    legacyProofDebt,
    ...(input.composition ? { composition: structuredClone(input.composition) } : {}),
    ...(compositionLineage ? { compositionLineage } : {}),
    ...(input.compositionRevision ? { compositionRevision: structuredClone(input.compositionRevision) } : {}),
    ...(origin ? { originatingRun: origin } : {}),
  };
}

/** Dormant server-only acceptance helper. Not connected to Create/enqueue. */
export async function resolveCompositionExecutionPolicyV2(input: { authority: AcceptedCompositionV2; releaseId: string;
  predecessorContext?: CompositionPredecessorContextV2;
  generation?: Omit<PrepareComponentGenerationPlanV2Input, 'authority'> & { pendingResolution: ComponentGenerationPlanV2 } }): Promise<AcceptedBuildExecutionPolicyV1> {
  const snapshot = structuredClone(input);
  const authority = await validateAcceptedCompositionV2(snapshot.authority, snapshot.predecessorContext);
  if (!/^[a-f0-9]{64}$/.test(snapshot.releaseId)) throw new PlatformError('validation', 'V2 private release identity required');
  let generation: { kind: 'bounded-component-generation'; pendingResolution: ComponentGenerationPlanV2; generationPolicyDigest: string } | undefined;
  if (snapshot.generation) {
    const { pendingResolution, ...planInput } = snapshot.generation;
    if (authority.predecessor !== null || authority.version !== 1 || !authority.unresolvedSlots.length) throw new PlatformError('validation', 'bounded generation requires fresh unresolved authority');
    const plan = await validateComponentGenerationPlanV2(pendingResolution, { ...planInput, authority });
    if (canonicalJson(plan.budget) !== canonicalJson(authority.generationBudget)) throw new PlatformError('validation', 'bounded generation limits changed');
    generation = { kind: 'bounded-component-generation', pendingResolution: plan, generationPolicyDigest: plan.policyDigest };
  }
  return { schemaVersion: 1, executionKind: authority.predecessor ? 'code-iterate' : 'code-create',
    acceptanceMode: 'measured-first-playable', pipelineSnapshot: compositionPrivatePipelineSnapshotV2(),
    providerPolicy: generation ? 'bounded-component-generation' : 'zero-provider', sourceIdentityRequired: false, legacyProofDebt: [],
    compositionV2: generation ? { route: 'private-composition-v2', authority, releaseId: snapshot.releaseId, ...generation }
      : { route: 'private-composition-v2', authority, releaseId: snapshot.releaseId } };
}
